import {
  LOG_LEVEL_ERROR,
  LOG_LEVEL_WARN,
  type LogLevel,
  type RequestErrorMetadata,
} from 'typings'

import {
  hasLoggedRequestError,
  isResponseLike,
  toLoggableError,
} from './loggableError'

const NETWORK_ERROR =
  /failed to fetch|networkerror|load failed|network request failed/i

export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (error.name === 'AbortError' || error.name === 'NetworkError') return true

  return error instanceof TypeError && NETWORK_ERROR.test(error.message)
}

function isRequestShaped(error: unknown): boolean {
  return (
    hasLoggedRequestError(error) ||
    isResponseLike(error) ||
    isNetworkError(error)
  )
}

/** True when this value, or an error wrapped around it, came from a request. */
export function isRequestFailure(
  error: unknown,
  seen = new Set<unknown>()
): boolean {
  return findRequestFailure(error, seen) !== undefined
}

export function findRequestFailure(
  error: unknown,
  seen = new Set<unknown>()
): unknown | undefined {
  if (error === null || typeof error !== 'object') return undefined
  if (seen.has(error)) return undefined

  seen.add(error)

  const cause = (error as { cause?: unknown }).cause
  const nested = findRequestFailure(cause, seen)

  if (nested !== undefined) return nested

  if (cause !== null && typeof cause === 'object' && 'error' in cause) {
    const fromCause = findRequestFailure(
      (cause as { error?: unknown }).error,
      seen
    )

    if (fromCause !== undefined) return fromCause
  }

  if (isRequestShaped(error)) return error

  return undefined
}

export function toRequestLog(error: unknown): {
  message: string
  level: LogLevel
  metadata: RequestErrorMetadata
} {
  const source = findRequestFailure(error) ?? error
  const loggable = toLoggableError(source)
  const status = loggable.status
  const metadata: RequestErrorMetadata = {
    url: loggable.url ?? '',
    statusCode: typeof status === 'number' ? String(status) : '0',
  }

  if (loggable.code) metadata.errorCode = loggable.code

  return {
    message: isResponseLike(source)
      ? source.statusText ?? ''
      : loggable.message,
    level:
      typeof status === 'number' && status > 499
        ? LOG_LEVEL_ERROR
        : LOG_LEVEL_WARN,
    metadata,
  }
}
